import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { memo } from "react";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { useTeamNotificationStream } from "./team-notification-stream-provider";

/**
 * NotificationSyncHandlers
 *
 * Registers built-in sync handlers for messages, conversations, and threads.
 * This component subscribes to notification events and syncs them to TanStack DB collections.
 *
 * Must be placed AFTER:
 * - TeamNotificationStreamProvider (provides the stream)
 * - GlobalConversationStoreProvider (provides the collections)
 *
 * Handles:
 * - messageCreated: Insert new messages into the conversation
 * - conversationCreated: Insert new conversations
 * - conversationUpdated: Update conversation status, assignment, priority, read status
 * - threadCreated: Insert new threads
 */
export const NotificationSyncHandlers = memo(
  function NotificationSyncHandlers() {
    const collections = useGlobalConversationCollections();

    // Message created handler
    useTeamNotificationStream("messageCreated", (event) => {
      if (!collections.messages.isReady) {
        return;
      }

      collections.messages.utils.writeInsert({
        ...event.message,
        sentAt: new Date(event.message.sentAt as unknown as string),
        conversationId: event.conversationId,
      });
    });

    // Conversation created handler
    useTeamNotificationStream("conversationCreated", (event) => {
      if (!collections.conversationsList.isReady) {
        return;
      }

      collections.conversationsList.utils.writeInsert({
        ...event.conversation,
      });
    });

    // Conversation updated handler
    useTeamNotificationStream("conversationUpdated", (event) => {
      if (!collections.conversationsList.isReady) {
        return;
      }

      collections.conversationsList.utils.writeUpdate(
        event.conversationId,
        (existingItem: ConversationListItemResponse | undefined) => {
          if (!existingItem) {
            return existingItem;
          }

          switch (event.updateType) {
            case "status_changed":
              return { ...existingItem, status: event.newStatus };
            case "assigned":
              return { ...existingItem, mongoAssigneeId: event.newAssignee };
            case "priority_changed":
              return { ...existingItem, priority: event.newPriority };
            case "read":
              return { ...existingItem, readAt: new Date() };
            case "tag_added":
            case "tag_removed":
              // Tags are handled separately - might need to refetch
              return existingItem;
            default:
              return existingItem;
          }
        },
      );
    });

    // Thread created handler
    useTeamNotificationStream("threadCreated", (event) => {
      if (!collections.threads.isReady) {
        return;
      }

      collections.threads.utils.writeInsert({ ...event.thread });
    });

    return null;
  },
);
